import { useState, useRef, useEffect } from "react";
import { ChatCircleDots, PaperPlaneRight, X } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import api from "../lib/api";

// Proactive engagement prompts that cycle while the widget is idle.
const ENGAGEMENT_PROMPTS = [
  "Curious about our flagship Leadership programmes? I can walk you through them.",
  "Wondering which free course to start with? Ask me — I'll match one to your goals.",
  "Need help picking a path? Tell me your role and I'll suggest the best fit.",
  "Want to know exactly what your peers in the SBL POD network are sharing this week? Ask me.",
  "Got 2 minutes? Let me show you around School Builder Lab Africa.",
];

const TEASE_DELAY_MS = 6000;   // first tease appears after 6s
const TEASE_CYCLE_MS = 8000;   // cycle to next prompt every 8s

export default function SblChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: "Hi there! I'm the SBL assistant. Ask me anything about School Builder Lab Africa — our programmes, courses, pricing, or how to apply." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());

  // Proactive tease bubble state
  const [teaseVisible, setTeaseVisible] = useState(false);
  const [teaseIndex, setTeaseIndex] = useState(0);
  const [teaseDismissed, setTeaseDismissed] = useState(() => {
    try { return sessionStorage.getItem("sbl_chat_tease_dismissed_v1") === "1"; }
    catch { return false; }
  });

  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Show the tease bubble after a short delay, then cycle through prompts.
  useEffect(() => {
    if (open || teaseDismissed) {
      setTeaseVisible(false);
      return;
    }
    const showTimer = setTimeout(() => setTeaseVisible(true), TEASE_DELAY_MS);
    return () => clearTimeout(showTimer);
  }, [open, teaseDismissed]);

  useEffect(() => {
    if (!teaseVisible || open || teaseDismissed) return;
    const interval = setInterval(() => {
      setTeaseIndex(i => (i + 1) % ENGAGEMENT_PROMPTS.length);
    }, TEASE_CYCLE_MS);
    return () => clearInterval(interval);
  }, [teaseVisible, open, teaseDismissed]);

  const dismissTease = (e) => {
    e.stopPropagation();
    setTeaseVisible(false);
    setTeaseDismissed(true);
    try { sessionStorage.setItem("sbl_chat_tease_dismissed_v1", "1"); } catch (err) { /* noop */ }
  };

  const openFromTease = () => {
    setTeaseVisible(false);
    setOpen(true);
    // Seed the conversation with the prompt the user clicked, so the bot can answer it.
    const seed = ENGAGEMENT_PROMPTS[teaseIndex];
    setMessages(prev => prev.length === 1 ? [...prev, { role: "bot", text: seed }] : prev);
  };

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/sbl-chat", { message: msg, session_id: sessionId });
      setMessages(prev => [...prev, { role: "bot", text: data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: "bot", text: "Sorry, I couldn't connect. Please email support@schoolbuilderlab.net.ng" }]);
    }
    setLoading(false);
  };

  return (
    <>
      {/* Floating trigger button + proactive tease bubble */}
      {!open && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2" data-testid="sbl-chat-launcher">
          {teaseVisible && !teaseDismissed && (
            <button
              type="button"
              onClick={openFromTease}
              className="relative max-w-[260px] sm:max-w-[300px] bg-white border border-slate-200 shadow-xl px-4 py-3 pr-7 text-left text-xs sm:text-sm text-slate-700 leading-relaxed rounded-tl-lg rounded-tr-lg rounded-bl-lg animate-in fade-in slide-in-from-bottom-2 duration-300"
              data-testid="sbl-chat-tease"
            >
              <span className="block font-semibold text-slate-900 mb-0.5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                SBL Assistant
              </span>
              <span className="block">
                {ENGAGEMENT_PROMPTS[teaseIndex]}
              </span>
              <span className="absolute top-1.5 right-1.5">
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Dismiss"
                  onClick={dismissTease}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") dismissTease(e); }}
                  className="text-slate-400 hover:text-slate-700 inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-slate-100 cursor-pointer"
                  data-testid="sbl-chat-tease-dismiss"
                >
                  <X size={12} weight="bold" />
                </span>
              </span>
              <span className="absolute -bottom-1.5 right-6 w-3 h-3 bg-white border-r border-b border-slate-200 transform rotate-45" />
            </button>
          )}

          <button
            onClick={() => setOpen(true)}
            className="bg-blue-800 text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg hover:bg-blue-900 transition-all hover:scale-105 group relative"
            data-testid="sbl-chat-trigger"
          >
            <ChatCircleDots size={26} weight="fill" />
            <span className="absolute -top-10 right-0 bg-slate-900 text-white text-xs px-3 py-1.5 rounded-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-md">
              Ask Me About SBL
            </span>
            {teaseVisible && !teaseDismissed && (
              <span className="absolute top-0 right-0 w-3 h-3 bg-amber-400 border-2 border-white rounded-full animate-pulse" />
            )}
          </button>
        </div>
      )}

      {/* Chat window */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[360px] sm:w-[400px] bg-white border border-slate-200 shadow-2xl flex flex-col" style={{ height: "480px" }} data-testid="sbl-chat-window">
          {/* Header */}
          <div className="bg-blue-800 text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <ChatCircleDots size={20} weight="fill" />
              <div>
                <p className="text-sm font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>SBL Assistant</p>
                <p className="text-[10px] text-white/70">Ask me anything about School Builder Lab Africa</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white" data-testid="sbl-chat-close">
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-blue-800 text-white rounded-tl-lg rounded-tr-lg rounded-bl-lg"
                    : "bg-white border border-slate-200 text-slate-700 rounded-tl-lg rounded-tr-lg rounded-br-lg"
                }`}>
                  {m.text.split(/(\*\*.*?\*\*)/).map((part, j) =>
                    part.startsWith("**") && part.endsWith("**")
                      ? <strong key={j}>{part.slice(2, -2)}</strong>
                      : <span key={j} style={{ whiteSpace: "pre-wrap" }}>{part}</span>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 text-slate-400 px-3 py-2 text-sm rounded-tl-lg rounded-tr-lg rounded-br-lg">
                  <span className="animate-pulse">Thinking...</span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 px-3 py-2 flex gap-2 bg-white flex-shrink-0">
            <Input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && send()}
              placeholder="Type your question..."
              className="rounded-none text-sm flex-1"
              disabled={loading}
              data-testid="sbl-chat-input"
            />
            <Button
              onClick={send}
              disabled={loading || !input.trim()}
              className="bg-blue-800 text-white hover:bg-blue-900 rounded-none px-3"
              data-testid="sbl-chat-send"
            >
              <PaperPlaneRight size={16} weight="fill" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
